import { exportToMultiFile, multiFileToExport, multiFileToContents, parseMultiFile, MULTIFILE_NAMES, SYNC_METADATA_FILE, } from '../providers/multifile';
const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';
const FOLDER_NAME = 'TrackMind';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
const BACKUP_FILE = 'trackmind-backup.json';
// Backup bookkeeping lives in its OWN file so the engine's saveSyncMetadata
// (which rewrites sync-metadata.json wholesale) can never clobber it — the
// old implementation lost lastBackupTime on every sync and rewrote the full
// backup each time as a result.
const BACKUP_META_FILE = 'backup-metadata.json';
const BACKUP_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // weekly
/** Escape a value for a Drive query string ('...' literal). */
function q(value) {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}
function randomToken() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
export class GoogleDriveAPI {
    providerType = 'google';
    config;
    folderId = null;
    /** name → Drive file id, primed by initialize()/lookups */
    fileIds = new Map();
    constructor(config) {
        this.config = config;
    }
    // ────────────────────────────────────────────────────────────────────
    // HTTP plumbing
    // ────────────────────────────────────────────────────────────────────
    async request(url, options = {}, asText = false) {
        const doFetch = async () => {
            const token = await this.config.getAccessToken();
            return fetch(url, {
                ...options,
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...options.headers,
                },
            });
        };
        let response = await doFetch();
        // One retry on 401: the token may have expired between the accessor's
        // freshness check and the request landing.
        if (response.status === 401) {
            response = await doFetch();
        }
        if (!response.ok) {
            const body = await response.text().catch(() => '');
            throw new Error(`Drive API error (${response.status}): ${body.slice(0, 300)}`);
        }
        return (asText ? response.text() : response.json());
    }
    // ────────────────────────────────────────────────────────────────────
    // Folder + file primitives
    // ────────────────────────────────────────────────────────────────────
    /** The folder this instance reads and writes. `TrackMind` unless overridden. */
    get folderName() {
        return this.config.folderName?.trim() || FOLDER_NAME;
    }
    /** Find-or-create the visible TrackMind folder. Idempotent. */
    async initialize() {
        if (this.folderId)
            return;
        const query = `name='${q(this.folderName)}' and mimeType='${FOLDER_MIME}' and 'root' in parents and trashed=false`;
        const found = await this.request(`${DRIVE_API}/files?q=${encodeURIComponent(query)}&fields=files(id,name)&pageSize=1`);
        if (found.files?.[0]) {
            this.folderId = found.files[0].id;
        }
        else {
            const created = await this.request(`${DRIVE_API}/files?fields=id`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: this.folderName, mimeType: FOLDER_MIME, parents: ['root'] }),
            });
            this.folderId = created.id;
        }
        // Prime the name→id cache with one listing (paginated).
        this.fileIds.clear();
        let pageToken;
        do {
            const listQuery = `'${q(this.folderId)}' in parents and trashed=false`;
            const page = await this.request(`${DRIVE_API}/files?q=${encodeURIComponent(listQuery)}&fields=nextPageToken,files(id,name)&pageSize=100${pageToken ? `&pageToken=${pageToken}` : ''}`);
            for (const f of page.files ?? []) {
                this.fileIds.set(f.name, f.id);
            }
            pageToken = page.nextPageToken;
        } while (pageToken);
    }
    async ensureFolder() {
        if (!this.folderId) {
            await this.initialize();
        }
        return this.folderId;
    }
    async findFileId(name) {
        if (this.fileIds.has(name))
            return this.fileIds.get(name);
        const folderId = await this.ensureFolder();
        const query = `name='${q(name)}' and '${q(folderId)}' in parents and trashed=false`;
        const found = await this.request(`${DRIVE_API}/files?q=${encodeURIComponent(query)}&fields=files(id,name)&pageSize=1`);
        const id = found.files?.[0]?.id ?? null;
        if (id)
            this.fileIds.set(name, id);
        return id;
    }
    async download(fileId) {
        return this.request(`${DRIVE_API}/files/${fileId}?alt=media`, {}, true);
    }
    async uploadNew(name, content) {
        const folderId = await this.ensureFolder();
        const boundary = 'trackmind_boundary_' + Math.random().toString(36).slice(2);
        const body = [
            `--${boundary}`,
            'Content-Type: application/json; charset=UTF-8',
            '',
            JSON.stringify({ name, parents: [folderId], mimeType: 'application/json' }),
            `--${boundary}`,
            'Content-Type: application/json; charset=UTF-8',
            '',
            content,
            `--${boundary}--`,
        ].join('\r\n');
        const created = await this.request(`${DRIVE_UPLOAD_API}/files?uploadType=multipart&fields=id`, {
            method: 'POST',
            headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
            body,
        });
        this.fileIds.set(name, created.id);
        return created.id;
    }
    async uploadExisting(fileId, content) {
        await this.request(`${DRIVE_UPLOAD_API}/files/${fileId}?uploadType=media`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json; charset=UTF-8' },
            body: content,
        });
    }
    /** Create-or-update a file by name. */
    async putFileByName(name, content) {
        const existing = await this.findFileId(name);
        if (existing) {
            try {
                await this.uploadExisting(existing, content);
                return;
            }
            catch (error) {
                // Cached id may be stale (file deleted externally) — refetch once.
                this.fileIds.delete(name);
                const fresh = await this.findFileId(name);
                if (fresh) {
                    await this.uploadExisting(fresh, content);
                    return;
                }
                if (!(error instanceof Error) || !/404/.test(error.message))
                    throw error;
            }
        }
        await this.uploadNew(name, content);
    }
    async readFileByName(name) {
        const id = await this.findFileId(name);
        if (!id)
            return null;
        try {
            return await this.download(id);
        }
        catch (error) {
            if (error instanceof Error && /404/.test(error.message)) {
                this.fileIds.delete(name);
                return null;
            }
            throw error;
        }
    }
    // ────────────────────────────────────────────────────────────────────
    // CloudStorageProvider: export data (shared multi-file layout)
    // ────────────────────────────────────────────────────────────────────
    async loadExportData() {
        await this.ensureFolder();
        const core = await this.readFileByName('core.json');
        if (!core)
            return null;
        const rest = MULTIFILE_NAMES.filter((n) => n !== 'core.json');
        const bodies = await Promise.all(rest.map((n) => this.readFileByName(n)));
        const contents = { 'core.json': core };
        rest.forEach((name, i) => {
            contents[name] = bodies[i];
        });
        return multiFileToExport(parseMultiFile(contents));
    }
    async saveExportData(data, _message) {
        await this.ensureFolder();
        const contents = multiFileToContents(exportToMultiFile(data));
        // Unlike GitHub (where each write is a commit and concurrent commits
        // conflict), Drive files are independent — write all six in parallel.
        await Promise.all(MULTIFILE_NAMES.map((name) => this.putFileByName(name, contents[name])));
        // Drive has no commit sha; a random token is this write's sync ref.
        return randomToken();
    }
    // ────────────────────────────────────────────────────────────────────
    // CloudStorageProvider: sync metadata + change detection
    // ────────────────────────────────────────────────────────────────────
    async getSyncMetadata() {
        const raw = await this.readFileByName(SYNC_METADATA_FILE);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    async saveSyncMetadata(metadata) {
        await this.putFileByName(SYNC_METADATA_FILE, JSON.stringify(metadata, null, 2));
    }
    async getRemoteRef() {
        const metadata = await this.getSyncMetadata();
        return metadata?.lastSyncRef ?? null;
    }
    async hasRemoteChanges(lastSyncRef) {
        const remoteRef = await this.getRemoteRef();
        if (!remoteRef)
            return false;
        return remoteRef !== lastSyncRef;
    }
    // ────────────────────────────────────────────────────────────────────
    // CloudStorageProvider: raw files (finance-*.json etc.)
    // ────────────────────────────────────────────────────────────────────
    async readRawFile(path) {
        await this.ensureFolder();
        return this.readFileByName(path);
    }
    async writeRawFile(path, content, _message) {
        await this.ensureFolder();
        await this.putFileByName(path, content);
    }
    // ────────────────────────────────────────────────────────────────────
    // Weekly backup (Drive has no history; GitHub relies on commits)
    // ────────────────────────────────────────────────────────────────────
    async createBackupIfNeeded(data) {
        const raw = await this.readFileByName(BACKUP_META_FILE);
        if (raw) {
            try {
                const meta = JSON.parse(raw);
                if (meta.lastBackupTime) {
                    const elapsed = Date.now() - new Date(meta.lastBackupTime).getTime();
                    if (elapsed < BACKUP_INTERVAL_MS)
                        return false;
                }
            }
            catch {
                // corrupt bookkeeping — just take a fresh backup
            }
        }
        await this.putFileByName(BACKUP_FILE, JSON.stringify(data, null, 2));
        await this.putFileByName(BACKUP_META_FILE, JSON.stringify({ lastBackupTime: new Date().toISOString() }, null, 2));
        return true;
    }
    /** Read the weekly backup (recovery path). */
    async loadBackupData() {
        const raw = await this.readFileByName(BACKUP_FILE);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    /** List everything in the TrackMind folder (debug/inspection). */
    async listFiles() {
        const folderId = await this.ensureFolder();
        const out = [];
        let pageToken;
        do {
            const listQuery = `'${q(folderId)}' in parents and trashed=false`;
            const page = await this.request(`${DRIVE_API}/files?q=${encodeURIComponent(listQuery)}&fields=nextPageToken,files(id,name,mimeType,modifiedTime,size)&pageSize=100${pageToken ? `&pageToken=${pageToken}` : ''}`);
            out.push(...(page.files ?? []));
            pageToken = page.nextPageToken;
        } while (pageToken);
        return out;
    }
}
//# sourceMappingURL=api.js.map